import React, { useState, useCallback, useEffect, useRef, useMemo } from "react"
import { Form } from "@unform/web"
import { FormHandles } from "@unform/core"
import api from "../../services/api"
import * as Yup from "yup"
import { useToast } from "../../hooks/toast"
import getValidationErrors from "../../utils/getValidationErrors"

import { FiRotateCcw, FiSearch, FiUsers } from "react-icons/fi"

import IClasses from "../../models/IClasses"

import {
  Container,
  Content,
  GroupBox,
  Main,
  CalendarColumn,
  StudentsColumn,
  Calendar,
  SearchButton,
  AbsenceCard,
  AbsenceButton,
} from "./styles"

import { ptBR } from "date-fns/locale"

import { DayPicker } from "react-day-picker"
import Header from "../../components/Header"
import Sidebar from "../../components/Sidebar"
import Autocomplete from "../../components/AutocompleteUnform"
import ConfirmationModal from "../../components/ConfirmationModal"

import IStudents from "../../models/IStudents"
import IStudentAbsences from "../../models/IStudentAbsences"
import returnBackendErrors from "../../utils/returnBackendError"

interface Options {
  id: string
  description: string
}

interface SearchClassFormData {
  class_id: string
}

interface AbsenceMap {
  isAbsent: boolean
  absence_id: string
}

const Absences: React.FC = () => {
  const formRef = useRef<FormHandles>(null)

  const { addToast } = useToast()

  const [modalOpen, setModalOpen] = useState(false)

  const [selectedDate, setSelectedDate] = useState(new Date())
  const [students, setStudents] = useState<IStudents[]>([])
  const [classes, setClasses] = useState<Options[]>([])
  const [dayAbsences, setDayAbsences] = useState<IStudentAbsences[]>([])

  const [selectedClassId, setSelectedClassId] = useState<string | null>(null)

  const [selectedAbsence, setSelectedAbsence] = useState("")

  const absencesMap = useMemo(() => {
    const map: Record<string, AbsenceMap> = {}
    dayAbsences.forEach((absence) => {
      map[absence.student_id] = {
        isAbsent: absence.absent,
        absence_id: absence.id,
      }
    })
    return map
  }, [dayAbsences])

  const fetchData = useCallback(async () => {
    const response = await api.get("/classes/all")

    const schoolClasses: IClasses[] = response.data.classes

    const formattedClasses = schoolClasses.map((schoolClass) => {
      return { id: schoolClass.id, description: schoolClass.name }
    })

    setClasses(formattedClasses)
  }, [])

  const fetchAbsences = useCallback(
    async (class_id: string) => {
      const response = await api.get("/absences/list-by-date", {
        params: {
          date: `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${selectedDate
            .getDate()
            .toString()
            .padStart(2, "0")}`,
          class_id,
        },
      })

      setDayAbsences(response.data.absences)
    },
    [selectedDate]
  )

  const handleDateChange = useCallback((day: Date) => {
    setSelectedDate(day)
  }, [])

  const handleAbsence = useCallback(
    async (
      absent: boolean,
      student: IStudents,
      e: React.MouseEvent<HTMLButtonElement>
    ) => {
      e.stopPropagation()

      try {
        await api.post("/absence", {
          date: `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${selectedDate
            .getDate()
            .toString()
            .padStart(2, "0")}`,
          absent,
          student_id: student.id,
        })

        fetchAbsences(student.class_id)

        addToast({
          type: "success",
          title: "Parabéns",
          description: "Falta/presença cadastrada com sucesso",
        })
      } catch (err) {
        addToast({
          title: "Erro",
          type: "error",
          description: returnBackendErrors(err),
        })
      }
    },
    [selectedDate, addToast, fetchAbsences]
  )

  const handleDeleteAbsence = useCallback(
    async (absence_id: string, class_id: string) => {
      try {
        await api.delete(`/absence/${absence_id}`)

        fetchAbsences(class_id)
      } catch (error) {
        addToast({
          title: "Erro",
          type: "error",
          description: returnBackendErrors(error),
        })
      }
    },
    [fetchAbsences, addToast]
  )

  const handleAddObs = useCallback(
    async (obs: string) => {
      try {
        await api.patch("/absences/update-obs", {
          id: selectedAbsence,
          obs,
        })
      } catch (error) {
        addToast({
          title: "Erro",
          type: "error",
          description: returnBackendErrors(error),
        })

        return
      }

      addToast({
        type: "success",
        title: "Observação adicionada com sucesso",
        description: "Parabéns! Observação adicionada com sucesso",
      })

      setModalOpen(false)
    },
    [selectedAbsence, addToast]
  )

  const handleSubmit = useCallback(
    async (data: SearchClassFormData) => {
      try {
        formRef.current?.setErrors({})

        const schema = Yup.object().shape({
          class_id: Yup.string().required("Turma do aluno é obrigatória"),
        })

        await schema.validate(data, {
          abortEarly: false,
        })

        const response = await api.get(`/students-by-class`, {
          params: {
            class_id: data.class_id,
          },
        })

        setStudents(response.data.students)
        setSelectedClassId(data.class_id)
      } catch (err) {
        addToast({
          type: "error",
          title: "Erro na Pesquisa",
          description:
            "Ocorreu um erro ao pesquisar, tente novamente mais tarde",
        })

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err)

          formRef.current?.setErrors(errors)

          return
        }
      }
    },
    [addToast]
  )

  const handleOpenDialog = useCallback(
    (student: IStudents) => {
      if (!absencesMap[student.id]) {
        addToast({
          type: "error",
          title: "Erro ao adicionar observação",
          description:
            "Não existe falta cadastrada para esse aluno para adicionar a observação, verifique",
        })

        return
      }

      setModalOpen(true)
      setSelectedAbsence(absencesMap[student.id].absence_id)
    },
    [absencesMap, addToast]
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (selectedClassId) {
      fetchAbsences(selectedClassId)
    }
  }, [selectedDate, fetchAbsences, selectedClassId])

  return (
    <Container>
      <Header />

      <Main>
        <Sidebar />

        <Content>
          <h1>Cadastro de Faltas</h1>

          <GroupBox>
            <CalendarColumn>
              <Calendar>
                <DayPicker
                  mode="single"
                  fixedWeeks
                  onDayClick={handleDateChange}
                  locale={ptBR}
                  disabled={{ dayOfWeek: [0, 6] }}
                  selected={selectedDate}
                />
              </Calendar>

              <Form ref={formRef} onSubmit={handleSubmit}>
                <Autocomplete
                  name="class_id"
                  icon={FiUsers}
                  type="text"
                  placeholder="Turma"
                  suggestions={classes}
                  containerStyle={{ flex: 1 }}
                />

                <SearchButton type="submit">
                  <FiSearch />
                </SearchButton>
              </Form>
            </CalendarColumn>

            <StudentsColumn>
              {students.map((student) => (
                <div
                  key={student.id}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <AbsenceCard
                    onClick={() => {
                      handleOpenDialog(student)
                    }}
                  >
                    <p>{student.name}</p>

                    <div>
                      <AbsenceButton
                        type="button"
                        className="present"
                        disabled={absencesMap.hasOwnProperty(student.id)}
                        isAbsent={absencesMap[student.id]?.isAbsent}
                        onClick={(e) => handleAbsence(false, student, e)}
                      >
                        Presente
                      </AbsenceButton>

                      <AbsenceButton
                        type="button"
                        className="absent"
                        disabled={absencesMap.hasOwnProperty(student.id)}
                        isAbsent={absencesMap[student.id]?.isAbsent}
                        onClick={(e) => handleAbsence(true, student, e)}
                      >
                        Falta
                      </AbsenceButton>
                    </div>
                  </AbsenceCard>

                  <FiRotateCcw
                    onClick={() =>
                      handleDeleteAbsence(
                        absencesMap[student.id]?.absence_id,
                        student.class_id
                      )
                    }
                  />
                </div>
              ))}
            </StudentsColumn>
          </GroupBox>
        </Content>
      </Main>

      <ConfirmationModal
        open={modalOpen}
        title="Adicionar Observação"
        message="Informe a observação para adicionar à falta"
        onConfirm={(obs) => {
          handleAddObs(obs)
        }}
        onCancel={() => setModalOpen(false)}
        onClose={() => setModalOpen(false)}
      />
    </Container>
  )
}

export default Absences
